import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useInvalidateProducts } from '@/hooks/api/0390_products';
import { useCreateProduct } from '@/hooks/api/0410_create_product';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  name: string;
  description: string;
  spot_available: boolean;
  spot_amount: number;
  monthly_available: boolean;
  monthly_amount: number;
  contract_term_available: boolean;
  delivery_date_available: boolean;
};

export const WorkspaceProductsNew = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('新規売上カテゴリ登録'), []);
  useEffect(() => setBreadcrumbs([{ label: `売上カテゴリ設定`, path: `/w/${_w_!.identifier}/setting/products` }]), []);

  const navigate = useNavigate();
  const { _w_, me } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { mutate, data, isPending, error } = useCreateProduct();
  const { invalidate } = useInvalidateProducts({ identifier: _w_!.identifier });

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      spot_available: true,
      monthly_available: true,
      contract_term_available: true,
      delivery_date_available: true,
    },
  });
  const spotAvailable = watch('spot_available');
  const monthlyAvailable = watch('monthly_available');

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('売上カテゴリを登録しました');
      invalidate(); // 売上カテゴリのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/products`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'product'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">新規売上カテゴリ登録</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>
                        売上カテゴリ名<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        className={`form-control ${errors.name && 'is-invalid'}`}
                        placeholder={'売上カテゴリ名'}
                        {...register('name', {
                          required: '入力して下さい。',
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>メモ</label>
                      <textarea
                        rows={5}
                        className={`form-control ${errors.description && 'is-invalid'}`}
                        {...register('description')}
                      ></textarea>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>一括請求金額入力フォーム</label>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            id="on-spot-available"
                            className="form-check-input"
                            {...register('spot_available')}
                          />
                          <label className="form-check-label" htmlFor="on-spot-available">
                            一括請求金額入力欄を表示します
                          </label>
                        </div>
                      </div>
                    </div>
                    {spotAvailable && (
                      <div className="col-12 col-md-5">
                        <FormGroup label="一括請求金額初期値">
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice onChange={onChange} value={value} error={errors.spot_amount} />
                              )}
                              control={control}
                              name="spot_amount"
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">
                            一括請求金額入力欄にデフォルトで入力したい値がある場合は設定してください。
                          </div>
                        </FormGroup>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>月額請求金額入力フォーム</label>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            id="on-monthly-available"
                            className="form-check-input"
                            {...register('monthly_available')}
                          />
                          <label className="form-check-label" htmlFor="on-monthly-available">
                            月額請求金額入力欄を表示します
                          </label>
                        </div>
                      </div>
                    </div>
                    {monthlyAvailable && (
                      <>
                        <div className="col-12 col-md-5">
                          <FormGroup label="月額請求金額初期値">
                            <div className="input-group">
                              <Controller
                                render={({ field: { onChange, value, ...field } }) => (
                                  <InputPrice onChange={onChange} value={value} error={errors.monthly_amount} />
                                )}
                                control={control}
                                name="monthly_amount"
                              />
                            </div>
                            <div className="text-xs text-muted mt-1">
                              月額請求金額入力欄にデフォルトで入力したい値がある場合は設定してください。
                            </div>
                          </FormGroup>
                        </div>
                      </>
                    )}
                  </div>
                  {/* <div className="form-group">
                    <label>Contract Term Form</label>
                    <div className="form-check form-switch">
                      <input type="checkbox" id="on-contract-term-available" className="form-check-input" {...register('contract_term_available')} />
                      <label className="form-check-label" htmlFor="on-contract-term-available">
                        契約期間の入力欄を表示します
                      </label>
                    </div>
                  </div> */}
                  <div className="form-group">
                    <label>納品日入力フォーム</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-delivery-date-available"
                        className="form-check-input"
                        {...register('delivery_date_available')}
                      />
                      <label className="form-check-label" htmlFor="on-delivery-date-available">
                        納品日の入力欄を表示します
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting/products`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>新規登録する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
